import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/nextjs';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { MediaProviders } from 'constants/media';
import {
  FileCategoryEnum,
  FileFormatEnum,
  useUpdateCoachImagesMutation,
  useUpdateCoachLessonAgeGroupsMutation,
} from 'types/generated/client';
import { useRequestStatus } from 'hooks/useRequestStatus';
import { useUploadSignedFile } from 'hooks/useUploadSignedFile';
import { useViewer } from 'hooks/useViewer';
import CloseIcon from 'svg/CloseIcon';
import Button, { ButtonText } from 'components/Button';
import Dropzone from 'components/Dropzone';
import MultiDropzone from 'components/MultiDropzone';
import ProfileItem from 'components/coaches-builder/ProfileItem';
import Modal from 'components/modals/Modal';
import { photosSchema } from './schemas';
import { FormModalProps } from '../types';

interface PhotoFormProps extends FormModalProps {}

type PhotoSchemaType = z.infer<typeof photosSchema>;

const PhotoFormModal: React.FC<PhotoFormProps> = ({ isOpen, handleClose, coachData, mode }) => {
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<PhotoSchemaType>({
    resolver: zodResolver(photosSchema),
  });

  const [updateCoachImages, { loading: updateCoachImagesLoading }] = useUpdateCoachImagesMutation();

  const { uploadSignedFile } = useUploadSignedFile();

  const viewer = useViewer();

  const userId = viewer.userId;

  const {
    isLoading: isImageLoading,
    setLoading: setImageLoading,
    setSuccess: setImageSuccess,
    setError: setImageError,
  } = useRequestStatus();

  const onSubmit = async (data: PhotoSchemaType) => {
    try {
      await updateCoachImages({
        variables: {
          userId,
          images: data.userFiles,
          ...data.photo,
        },
      });
      handleClose(true); // recalling coach api
    } catch (err) {
      captureException(err);
    }
  };

  useEffect(() => {
    if (!userId || !coachData) return;
    const userFiles = coachData.userFiles.map((userFile) => ({
      fileCategory: userFile.fileCategory,
      fileName: userFile.fileName,
      fileType: userFile.fileType,
      host: userFile.host,
      originalFileName: userFile.originalFileName ?? '',
      path: userFile.path,
      provider: userFile.provider,
      providerUrl: userFile.providerUrl,
      url: userFile.providerUrl,
      fileFormat: userFile.fileFormat,
      providerId: '',
      userId,
    }));

    setValue('photo', {
      profileImageFileName: coachData?.profileImageFileName || '',
      profileImagePath: coachData?.profileImagePath || '',
      profileImageProvider: coachData?.profileImageProvider || '',
      profileImageProviderId: coachData?.profileImageProviderId || '',
      profileImageProviderUrl: coachData?.profileImageProviderUrl || '',
      profileImageUrl: coachData?.profileImageUrl || coachData?.profileImageProviderUrl || '',
    });

    setValue('userFiles', userFiles);
  }, [coachData, reset, setValue, userId]);

  return (
    <Modal isOpen={isOpen} handleClose={handleClose} classNameMaxWidth="max-w-[80vw]">
      <div className="flex items-center justify-between border-b px-8 py-4">
        <h2 className="typography-product-heading-desktop">Coach photo gallery</h2>
        <ButtonText onClick={() => handleClose()}>
          <CloseIcon className="h-4 w-4" />
        </ButtonText>
      </div>
      <div className="flex flex-col px-4 md:px-10">
        <ProfileItem
          label="Profile photo"
          description="Give the community an easy way to recognize you."
          component={
            <>
              <div className="flex w-full gap-x-4">
                <Controller
                  control={control}
                  name="photo"
                  render={({ field: { onChange } }) => {
                    return (
                      <Dropzone
                        multiple={false}
                        isImage={true}
                        styles={`rounded-md`}
                        uploadedFileUrl={getValues('photo')?.profileImageProviderUrl || ''}
                        onSelectFiles={async (files) => {
                          try {
                            setImageLoading();
                            const file = files[0];
                            const response = await uploadSignedFile({ file });

                            if (response) {
                              onChange({
                                profileImageFileName: response.fileName,
                                profileImagePath: response.path,
                                profileImageProvider: MediaProviders.Cloudflare,
                                profileImageProviderId: response.fileName.split('.')[0],
                                profileImageProviderUrl: response.providerUrl,
                                profileImageUrl: response.url,
                              });
                            }
                          } catch (error) {
                            captureException(error);
                            setImageError('');
                          } finally {
                            setImageSuccess();
                          }
                        }}
                      />
                    );
                  }}
                />
              </div>
              <p className="bottom-1 mt-2 text-xs text-color-error sm:mt-0">
                {errors?.photo && errors?.photo?.profileImageProviderUrl?.message}
              </p>
            </>
          }
        />
        <ProfileItem
          label="Additional photos or videos"
          hasBottomBorder={false}
          description="Please ensure to include additional photos or videos of yourself playing or teaching to attract potential students."
          component={
            <>
              <div className="flex w-full gap-x-4">
                <Controller
                  control={control}
                  name="userFiles"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <MultiDropzone
                        multiple={true}
                        uploading={isImageLoading}
                        styles={`rounded-md`}
                        initialFiles={value || []}
                        maxFiles={8}
                        accept={{
                          'image/*': [],
                          'video/*': ['.mp4', '.mov'],
                        }}
                        onRemoveFile={(_, fileIndex) => {
                          const currentFiles = [...(value || [])];
                          currentFiles.splice(fileIndex, 1);
                          setValue('userFiles', currentFiles);
                        }}
                        onSelectFiles={async (files) => {
                          try {
                            setImageLoading();

                            const uploadPromises = files.map((file) => uploadSignedFile({ file }));
                            const payloads = await Promise.all(uploadPromises);
                            if (!payloads) return;

                            const updatedFiles = payloads
                              .map((payload) => {
                                captureException(
                                  new Error('+++ userFiles Payload Coach Settings +++'),
                                  {
                                    extra: { payload: JSON.stringify(payload, null, 2) },
                                  },
                                );

                                if (!payload || !payload.url) return null;

                                const fileFormat = payload?.fileType.startsWith('video/')
                                  ? FileFormatEnum.Video
                                  : FileFormatEnum.Image;
                                return {
                                  fileCategory: FileCategoryEnum.Coach,
                                  fileName: payload?.fileName || '',
                                  fileType: payload?.fileType || '',
                                  host: payload?.host || '',
                                  originalFileName: payload?.originalFileName || '',
                                  path: payload?.path || '',
                                  provider: payload?.provider || '',
                                  providerUrl: payload?.providerUrl || '',
                                  fileFormat: fileFormat,
                                  url: payload?.url,
                                  providerId: '',
                                  userId,
                                };
                              })
                              .filter(Boolean);
                            onChange([...(value || []), ...updatedFiles]);
                            setImageSuccess();
                          } catch (error) {
                            setImageError('');
                          }
                        }}
                      />
                    );
                  }}
                />
              </div>
              <p className="bottom-1 mt-2 text-xs text-color-error sm:mt-0">
                {errors?.userFiles && errors?.userFiles?.message}
              </p>
            </>
          }
        />
      </div>
      <div className=" flex justify-end border-t px-8 py-4">
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          className="w-fit"
          size="lg"
          loading={updateCoachImagesLoading || isImageLoading}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default PhotoFormModal;
